import React, { useEffect, useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import {
    Box,
    Button,
    CircularProgress,
    TextField,
    Typography,
    makeStyles,
    Theme,
} from '@material-ui/core'
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline'
import OpenInNewIcon from '@material-ui/icons/OpenInNew'

import { useRent } from '../../hooks/useRent'
import { Routes } from '../../navigation/config/routes'
import { FormSaveAction } from './FormSaveButton'
import { SetupArtifactShell } from './SetupArtifactShell'
import { SelectedSetupConfig, SetupConfigOption } from './setupArtifacts'

const useStyles = makeStyles((theme: Theme) => ({
    body: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1.5),
        width: '100%',
        padding: theme.spacing(0.5, 0, 1),
    },
    intro: {
        fontSize: '0.8125rem',
        color: theme.palette.text.secondary,
        lineHeight: 1.45,
    },
    amountField: {
        maxWidth: 180,
        '& .MuiInputBase-input': {
            fontSize: '0.9375rem',
        },
    },
    statusCard: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: theme.spacing(0.75),
        padding: theme.spacing(1.25, 1.5),
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 8,
        backgroundColor: theme.palette.background.paper,
        maxWidth: 420,
    },
    statusOk: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.75),
        fontSize: '0.8125rem',
        fontWeight: 600,
    },
    statusWarn: {
        fontSize: '0.8125rem',
        fontWeight: 600,
        color: theme.palette.error.main,
    },
    statusDetail: {
        fontSize: '0.8125rem',
        color: theme.palette.text.secondary,
        lineHeight: 1.45,
    },
    statusLoading: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        fontSize: '0.8125rem',
        color: theme.palette.text.secondary,
    },
}))

function feeAmountReady(amount: string): boolean {
    const parsed = Number.parseFloat(amount)
    return Number.isFinite(parsed) && parsed >= 0
}

function feeRequiresStripe(amount: string): boolean {
    const parsed = Number.parseFloat(amount)
    return Number.isFinite(parsed) && parsed > 0
}

export const ApplicationFeeTab: React.FC<{
    selected: SelectedSetupConfig | null
    onSelected: (config: SelectedSetupConfig) => void
    onClearSelection: () => void
    existing: SetupConfigOption[]
    onExistingChange: (next: SetupConfigOption[]) => void
    amount: string
    currency?: string
    onAmountChange: (next: string) => void
    contentLocked?: boolean
    artifactEditable?: boolean
    saveAction?: FormSaveAction
}> = ({
    selected,
    onSelected,
    onClearSelection,
    existing,
    onExistingChange,
    amount,
    currency = 'USD',
    onAmountChange,
    contentLocked = false,
    artifactEditable = true,
    saveAction,
}) => {
    const classes = useStyles()
    const { fetchStripeConnectAccount } = useRent()
    const [stripeLoading, setStripeLoading] = useState(true)
    const [stripeReady, setStripeReady] = useState(false)
    const readOnly =
        !!selected?.orgMandated || contentLocked || !artifactEditable

    useEffect(() => {
        let cancelled = false
        setStripeLoading(true)
        fetchStripeConnectAccount()
            .then((account) => {
                if (cancelled) return
                setStripeReady(Boolean(account?.charges_enabled))
            })
            .catch(() => {
                if (!cancelled) setStripeReady(false)
            })
            .finally(() => {
                if (!cancelled) setStripeLoading(false)
            })
        return () => {
            cancelled = true
        }
    }, [fetchStripeConnectAccount])

    const amountReady = feeAmountReady(amount)
    const chargesFee = feeRequiresStripe(amount)
    const readinessLabel = !selected
        ? undefined
        : !amountReady
        ? 'Enter a fee amount'
        : chargesFee && stripeLoading
        ? 'Checking Stripe…'
        : chargesFee && !stripeReady
        ? 'Stripe not connected'
        : 'Ready'

    return (
        <SetupArtifactShell
            kind="application_fee"
            selected={selected}
            onSelected={onSelected}
            onClearSelection={onClearSelection}
            existing={existing}
            onExistingChange={onExistingChange}
            discourageScratch
            contentLocked={contentLocked}
            readinessLabel={readinessLabel}
            saveAction={readOnly ? undefined : saveAction}
        >
            <Box className={classes.body}>
                <Typography className={classes.intro}>
                    Checkout timing is set in Compose → Leads Pipeline. A $0 fee
                    is valid and does not need Stripe; timing is locked to Never
                    until you charge more than $0.
                </Typography>
                {chargesFee && stripeLoading ? (
                    <Box className={classes.statusLoading}>
                        <CircularProgress size={16} />
                        Checking Stripe…
                    </Box>
                ) : chargesFee && stripeReady ? (
                    <Box className={classes.statusCard}>
                        <Typography className={classes.statusOk}>
                            <CheckCircleOutlineIcon style={{ fontSize: 16 }} />
                            Stripe connected
                        </Typography>
                        <Typography className={classes.statusDetail}>
                            Charges are enabled for this property.
                        </Typography>
                        <Button
                            size="small"
                            color="primary"
                            component={RouterLink}
                            to={Routes.manage.integrations}
                            endIcon={<OpenInNewIcon fontSize="small" />}
                        >
                            Open Integrations
                        </Button>
                    </Box>
                ) : chargesFee ? (
                    <Box className={classes.statusCard}>
                        <Typography className={classes.statusWarn}>
                            Stripe not connected
                        </Typography>
                        <Typography className={classes.statusDetail}>
                            Connect Stripe under Integrations before this
                            application fee setup can be finished. Applicants
                            should not reach checkout and find out the property
                            cannot collect payment.
                        </Typography>
                        <Button
                            size="small"
                            color="primary"
                            variant="contained"
                            component={RouterLink}
                            to={Routes.manage.integrations}
                            endIcon={<OpenInNewIcon fontSize="small" />}
                        >
                            Set Up Stripe
                        </Button>
                    </Box>
                ) : amountReady ? (
                    <Box className={classes.statusCard}>
                        <Typography className={classes.statusOk}>
                            <CheckCircleOutlineIcon style={{ fontSize: 16 }} />
                            No payment to collect
                        </Typography>
                        <Typography className={classes.statusDetail}>
                            A $0 fee does not require Stripe. Compose fee timing
                            stays on Never until you set an amount greater than
                            $0.
                        </Typography>
                    </Box>
                ) : null}
                {selected ? (
                    <TextField
                        className={classes.amountField}
                        label="Fee amount"
                        type="number"
                        size="small"
                        variant="outlined"
                        value={amount}
                        disabled={readOnly}
                        onChange={(event) => onAmountChange(event.target.value)}
                        onKeyDown={(event) => {
                            if (
                                event.key === 'Enter' &&
                                !readOnly &&
                                saveAction?.dirty
                            ) {
                                event.preventDefault()
                                saveAction.onSave()
                            }
                        }}
                        inputProps={{ min: 0, step: '0.01' }}
                        InputProps={{
                            startAdornment: (
                                <Box
                                    component="span"
                                    style={{
                                        marginRight: 6,
                                        color: 'rgba(0,0,0,0.54)',
                                        fontSize: '0.875rem',
                                    }}
                                >
                                    {currency}
                                </Box>
                            ),
                        }}
                    />
                ) : null}
            </Box>
        </SetupArtifactShell>
    )
}
